import { useState, type FormEvent } from "react";

export default function Contact() {
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    msg: string;
  }>({ type: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("company_alt") as string)?.trim() || "";
    const payload = new URLSearchParams();

    // build payload from real form fields only (honeypot excluded)
    form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[name],textarea[name]").forEach((el) => {
      const name = el.getAttribute("name") || "";
      if (!name || name === "company_alt") return;
      if (el.name === "email") {
        payload.append(name, (el.value || "").trim());
      } else {
        payload.append(name, el.value);
      }
    });

    // honeypot: pretend success but never send
    if (honeypot) {
      setStatus({ type: "success", msg: "已收到你的消息，我们会在 1-2 个工作日内与你联系！" });
      form.reset();
      return;
    }

    setLoading(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch("https://readdy.ai/api/form/daf9dsptto0ceqqnh8hg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (parsed?.code ? "" : responseText);
      const isSpam =
        /spam|form data is spam/i.test(responseText) ||
        /spam|form data is spam/i.test(serverMsg || "");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus({ type: "success", msg: "已收到你的消息，我们会在 1-2 个工作日内与你联系！" });
        form.reset();
      } else {
        setStatus({ type: "error", msg: serverMsg || "提交失败，请稍后重试或换个邮箱再试一次。" });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，提交未成功，请稍后再试。" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="contact" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-3xl bg-primary-500">
          <div className="relative grid gap-10 p-6 md:grid-cols-2 md:p-12">
            <div className="pointer-events-none absolute -right-10 -top-16 h-64 w-64 rounded-full bg-background-50/10 blur-2xl" />
            <div className="flex flex-col justify-center text-background-50">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm font-semibold">
                <i className="ri-mail-send-line" /> 联系与投稿
              </span>
              <h2 className="font-heading mt-4 text-2xl font-black leading-snug md:text-4xl">
                有好内容想分享？
                <br /> 或想给吃瓜爆网提建议？
              </h2>
              <p className="mt-4 max-w-md text-background-50/90">
                无论是推荐宝藏视频、合作交流还是意见反馈，都欢迎给我们留言。我们会认真阅读每一条消息。
              </p>
              <div className="mt-6 flex flex-col gap-3 text-sm">
                <span className="inline-flex items-center gap-2">
                  <i className="ri-mail-line" /> hello@chiguabaowang.example.com
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="ri-time-line" /> 内容团队每日 9:00 - 21:00 在线
                </span>
              </div>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 rounded-2xl bg-background-50 p-6 text-foreground-800"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium">
                  你的称呼
                  <input
                    type="text"
                    name="name"
                    placeholder="怎么称呼你"
                    className="mt-1 rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                    required
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium">
                  联系方式
                  <input
                    type="email"
                    name="email"
                    placeholder="邮箱地址"
                    className="mt-1 rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                    required
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                留言内容
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="想分享的看点 / 投稿内容 / 建议反馈……"
                  className="mt-1 resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                  required
                />
              </label>

              {/* honeypot */}
              <div className="hp-field">
                <label htmlFor="company_alt_field">
                  <input
                    type="text"
                    id="company_alt_field"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-1 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-base font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 正在发送…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" /> 提交留言
                  </>
                )}
              </button>

              {status.type === "success" && (
                <p className="flex items-center gap-2 rounded-lg bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-fill" /> {status.msg}
                </p>
              )}
              {status.type === "error" && (
                <p className="flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
                  <i className="ri-error-warning-fill" /> {status.msg}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}